/**
 * Google sign-in through Google Identity Services.
 *
 * The app only ever asks for read-only YouTube scope, and the token lives in
 * memory for the session — nothing is persisted but the credentials the user
 * pasted in themselves.
 */

import { OPTIONS } from '../config/defaults.ts';
import { loadScript } from '../lib/loaders.ts';
import { describeAuthError } from './youtube-errors.ts';

export interface TokenResponse {
  access_token?: string;
  expires_in?: number | string;
  error?: string;
}

interface TokenClient {
  requestAccessToken: () => void;
}

interface OAuth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    prompt: string;
    callback: (res: TokenResponse) => void;
    error_callback?: (err: { type?: string; message?: string }) => void;
  }) => TokenClient;
  revoke?: (token: string, done: () => void) => void;
}

interface GoogleGlobal {
  google?: { accounts?: { oauth2?: OAuth2 } };
}

const GIS_URL = 'https://accounts.google.com/gsi/client';
const ATTACH_TRIES = 40;
const ATTACH_WAIT = 50;

const oauth2Of = (): OAuth2 | undefined =>
  (globalThis as unknown as GoogleGlobal).google?.accounts?.oauth2;

/** Loads Google Identity Services, waiting for it to attach to the page. */
export async function loadGoogleIdentity(): Promise<OAuth2> {
  const ready = oauth2Of();
  if (ready) return ready;

  await loadScript(GIS_URL, 'gis-client');
  /* The library attaches asynchronously right after onload. */
  for (let i = 0; i < ATTACH_TRIES && !oauth2Of(); i++) {
    await new Promise<void>((r) => {
      setTimeout(r, ATTACH_WAIT);
    });
  }
  const oauth2 = oauth2Of();
  if (!oauth2) throw new Error('Google Identity Services failed to load.');
  return oauth2;
}

export interface AccessToken {
  token: string;
  /** Epoch milliseconds, already backed off a minute from the real expiry. */
  expiry: number;
}

/** Opens Google's consent popup. Must be called from a user gesture. */
export async function requestAccessToken(clientId: string): Promise<AccessToken> {
  const oauth2 = await loadGoogleIdentity();
  const res = await new Promise<TokenResponse>((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: OPTIONS.scope,
      prompt: '',
      callback: (r) => {
        if (r.error) reject(new Error(describeAuthError(r.error)));
        else resolve(r);
      },
      error_callback: (err) => {
        reject(new Error(describeAuthError(err.type ?? err.message)));
      },
    });
    client.requestAccessToken();
  });

  if (!res.access_token) throw new Error(describeAuthError(undefined));
  return {
    token: res.access_token,
    expiry: Date.now() + (Number(res.expires_in ?? 3600) - 60) * 1000,
  };
}

/** Best-effort token revocation. Signing out locally always succeeds. */
export function revokeToken(token: string | null): void {
  if (!token) return;
  try {
    oauth2Of()?.revoke?.(token, () => undefined);
  } catch {
    /* the token expires on its own soon enough */
  }
}
